import RoutePath from 'App/RoutePath';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSubdomainInfo } from 'LoggedOut/SignUp/useSubdomainInfo';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import LoadingIndicator from 'Shared/LoadingIndicator';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { useGetRegistrationMethods } from './hooks/useGetRegistrationMethods';
import { TypedRegistrationFlow } from './TypedRegistrationFlow';
import { MembershipIdVerification } from './MembershipIdVerification';
import { useVerifyUserForRegistration } from './hooks/useVerifyUserForRegistration';
import { useVerificationTracking } from './hooks/useVerificationTracking';
import {
  AccountVerificationSubRoutes,
  RegistrationFlow,
  RegistrationMethod,
  TrackedEntryPoint,
} from './constants';
import { RegistrationFlowState } from './types';

export const MEMBERSHIP_ID_PARAM = 'membership_id';

const getVerificationFlow = (
  registrationMethods: RegistrationMethod[],
): {
  verificationFlow:
    | RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID
    | RegistrationFlow.VERIFICATION_OPEN_EMAIL;
  registrationMethod:
    | RegistrationMethod.OPEN_EMAIL
    | RegistrationMethod.CLOSED_EMPLOYEE_ID;
} => {
  if (registrationMethods.includes(RegistrationMethod.CLOSED_EMPLOYEE_ID)) {
    return {
      verificationFlow: RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID,
      registrationMethod: RegistrationMethod.CLOSED_EMPLOYEE_ID,
    };
  }

  return {
    verificationFlow: RegistrationFlow.VERIFICATION_OPEN_EMAIL,
    registrationMethod: RegistrationMethod.OPEN_EMAIL,
  };
};

export const RegistrationAccountVerification = () => {
  const subdomain = getSubdomainFromUrl();
  const { registrationMethods, loading: registrationMethodsLoading } =
    useGetRegistrationMethods();
  const { groupId: organisationId, groupName } = useSubdomainInfo({
    subdomain,
  });

  const { t: translate } = useTranslation('shared');
  const { t: translateLoggedOut } = useTranslation('logged_out');
  const [error, setError] = useState<string | undefined>();
  const [redirectState, setRedirectState] = useState({
    email: '',
    employeeId: '',
    redirectTo: '',
  });
  const history = useHistory();
  const location = useLocation();
  const membershipIdFlowEnabled = useFeatureFlag<boolean | undefined>(
    FEATURE_FLAGS.MEMBERSHIP_ID_VERIFICATION_FLOW,
  );

  const parentPath = RoutePath.AccountVerification;
  const { registrationMethod, verificationFlow } =
    getVerificationFlow(registrationMethods);

  const membershipId = new URLSearchParams(location.search).get(
    MEMBERSHIP_ID_PARAM,
  );

  const { manualTracker, membershipTracker, onStepViewed, onStepCompleted } =
    useVerificationTracking({
      membershipId,
      registrationMethods,
      registrationMethod,
    });

  type K = typeof verificationFlow;

  const isSSOOnlySignUp =
    registrationMethods.length === 1 &&
    registrationMethods.includes(RegistrationMethod.SSO);

  const [initialFlowDetails, setInitialFlowDetails] =
    useState<RegistrationFlowState[K]>();

  const {
    callVerifyUserForRegistration,
    loading: verifyUserForRegistrationLoading,
  } = useVerifyUserForRegistration({
    tracker: manualTracker,
    onError: () => setError(translate('errors.messages.oops_something_wrong')),
    onSuccess: () => {
      history.push({
        pathname: RoutePath.EmailConfirmation,
        state: redirectState,
      });
    },
  });

  const getRedirectPath = () => {
    if (verificationFlow === RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID) {
      return `${parentPath}/${AccountVerificationSubRoutes.Email}`;
    }

    return parentPath;
  };

  useEffect(() => {
    const { email, employeeId } = location.state || {};

    setInitialFlowDetails({ email, employeeId });
  }, [location.state]);

  useEffect(() => {
    setError(undefined);
  }, [location.pathname]);

  // Held back until methods load: getVerificationFlow otherwise defaults to the
  // open-email flow, briefly rendering the wrong form and reporting the wrong step.
  if (registrationMethodsLoading) return <LoadingIndicator />;

  if (isSSOOnlySignUp) {
    history.push(RoutePath.LoginWithSSO);

    return null;
  }

  if (!verificationFlow) return null;

  if (membershipId) {
    if (membershipIdFlowEnabled === undefined) return <LoadingIndicator />;

    if (membershipIdFlowEnabled) {
      return (
        <MembershipIdVerification
          tracker={membershipTracker}
          membershipId={membershipId}
          organisationName={groupName}
          showSSOOption={registrationMethods.includes(RegistrationMethod.SSO)}
          onInvalidLink={() => {
            const search = new URLSearchParams(location.search);
            search.delete(MEMBERSHIP_ID_PARAM);

            // Preserve the entry point so the fallback into the manual flow
            // stays attributed to the membership ID link the user arrived on.
            history.replace({
              pathname: location.pathname,
              search: search.toString(),
              state: {
                ...location.state,
                entryPoint: TrackedEntryPoint.MembershipId,
              },
            });

            setError(
              translateLoggedOut(
                'sign_up.membership_id_verification.invalid_link_notice',
              ),
            );
          }}
        />
      );
    }
  }

  const onVerificationFlowComplete = async (args: RegistrationFlowState[K]) => {
    if (!organisationId) return;

    setError(undefined);

    const { email } = args;
    const employeeId = 'employeeId' in args ? args.employeeId : '';

    setRedirectState({ email, employeeId, redirectTo: getRedirectPath() });

    await callVerifyUserForRegistration({
      employeeId,
      email,
      registrationMethod,
      organisationId,
    });
  };

  return (
    <TypedRegistrationFlow
      loading={verifyUserForRegistrationLoading}
      registrationFlow={verificationFlow}
      error={error}
      onEnd={onVerificationFlowComplete}
      initialFlowDetails={initialFlowDetails}
      parentPath={parentPath}
      onStepViewed={onStepViewed}
      onStepCompleted={onStepCompleted}
    />
  );
};
